import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { CompanyDossier } from '../../api/employerVerificationDashboardApi';
import { CompanyVerificationMap } from '../CompanyVerificationMap';
import { employerVerificationFinalChipColor } from '../../lib/employerVerificationFinalChip';
import {
  type DossierTabPreset,
  dossierHints,
  dossierResolutionLabel,
  finalOutcomeLabelStored,
  finalOutcomeTechnical,
} from '../../lib/employerVerificationCopy';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function finalChip(status: string | null): 'success' | 'error' | 'warning' | 'default' {
  return employerVerificationFinalChipColor(status);
}

function resolutionChipTone(
  resolution: string,
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  if (resolution === 'MATCHED_LOCATION_VERSION') return 'success';
  if (resolution === 'GEOCODE_CACHE_ONLY') return 'info';
  if (resolution === 'SNAPSHOT_NORMALIZED_ONLY') return 'warning';
  return 'default';
}

export type CompanyDossierEvidenceBodyProps = {
  d: CompanyDossier;
  variant: 'page' | 'drawer';
  /** When true with `variant="drawer"`, omits duplicate employer heading and Address/map accordion (parent renders {@link DossierAddressMapInline}). */
  workspaceSlice?: boolean;
  /** When true with `variant="drawer"` and `workspaceSlice`, replaces dossier accordions with tabs (Company / Location). */
  dossierTabs?: boolean;
  /** Restricts dossier tabs/panels for investigation workspace gate scope. Ignored when `dossierTabs` is false. */
  dossierTabPreset?: DossierTabPreset;
  onBackToOverview?: () => void;
};

export type DossierAddressMapInlineProps = {
  d: CompanyDossier;
  /** Larger map area with rounded corners for investigation workspace. */
  heroMap?: boolean;
};

/** Shared address + resolution + map block (drawer accordion and investigation workspace). */
export function DossierAddressMapInline({ d, heroMap = false }: DossierAddressMapInlineProps) {
  const marker = d.map.markers?.[0];
  return (
    <Stack spacing={heroMap ? 1.25 : 1.5}>
      <Tooltip title={d.verificationAddress.resolution.replace(/_/g, ' ')} placement="top" arrow>
        <Chip
          size="small"
          label={dossierResolutionLabel(d.verificationAddress.resolution)}
          color={resolutionChipTone(d.verificationAddress.resolution)}
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
      </Tooltip>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.58, fontWeight: 500 }}>
        {d.verificationAddress.formattedAddress ?? d.verificationAddress.normalizedKey ?? '—'}
      </Typography>
      {!heroMap && d.verificationAddress.latitude != null && d.verificationAddress.longitude != null ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {d.verificationAddress.latitude.toFixed(6)}, {d.verificationAddress.longitude.toFixed(6)}
        </Typography>
      ) : null}
      {!heroMap && (d.verificationAddress.latitude == null || d.verificationAddress.longitude == null) ? (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          Coordinates missing—the map hides until geocoding persists lat/lng.
        </Alert>
      ) : null}
      {!heroMap ? <Divider /> : null}
      {!heroMap ? (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Map · {d.map.markerPolicy.replace(/_/g, ' ').toLowerCase()}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {dossierHints.mapSingleSite}
          </Typography>
        </>
      ) : null}
      {marker ? (
        <Box
          sx={
            heroMap
              ? {
                  minHeight: 420,
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
                }
              : {}
          }
        >
          <CompanyVerificationMap
            lat={marker.lat}
            lng={marker.lng}
            title={marker.label}
            formattedAddress={d.verificationAddress.formattedAddress}
          />
        </Box>
      ) : (
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: heroMap ? '20px' : 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Pin needs saved coordinates—run geocoding or attach a persisted location version for the work address above.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

function accordionShell(title: string, children: ReactNode) {
  return (
    <Box sx={{ width: '100%' }}>
      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 44, px: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 2 }}>{children}</AccordionDetails>
      </Accordion>
    </Box>
  );
}

export function CompanyDossierEvidenceBody({
  d,
  variant,
  onBackToOverview,
  workspaceSlice = false,
  dossierTabs = false,
  dossierTabPreset = 'full',
}: CompanyDossierEvidenceBodyProps) {
  const marker = d.map.markers?.[0];
  const [dossierTab, setDossierTab] = useState(0);

  if (variant === 'drawer') {
    const hideEmployerHeader = workspaceSlice;
    const hideAddressAccordion = workspaceSlice;

    const companyPanel = d.employer ? (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          Employer ID{' '}
          <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: 'text.primary' }}>
            {d.employer.ceid}
          </Box>
          {d.employer.sector ? ` · Industry · ${d.employer.sector}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          Registry status {d.employer.registryStatus ?? '—'} · source {d.employer.registrySource ?? '—'}
        </Typography>
      </Stack>
    ) : (
      <Alert severity="warning" variant="outlined">
        Employer profile missing for this verification (no registry entity linkage yet).
      </Alert>
    );

    const locationPanel = (
      <Stack spacing={2}>
        {d.intelligenceSnapshot ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Location intelligence
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {(d.intelligenceSnapshot.placeName as string) ?? 'Place name unavailable'}{' '}
              {(d.intelligenceSnapshot.businessStatus as string)
                ? `· ${String(d.intelligenceSnapshot.businessStatus)}`
                : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
              Match score {(d.intelligenceSnapshot.intelligenceScore as number) ?? '—'}
              <br />
              Google types:{' '}
              {Array.isArray(d.intelligenceSnapshot.placeTypes)
                ? (d.intelligenceSnapshot.placeTypes as string[]).join(', ')
                : '—'}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No location intelligence snapshot for this verification.
          </Typography>
        )}
        {d.caches.geocode || d.caches.googlePlace ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              External lookups
            </Typography>
            {d.caches.geocode ? (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                Geocode cache · provider {(d.caches.geocode.provider as string) ?? '—'}
              </Typography>
            ) : null}
            {d.caches.googlePlace ? (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                Google Places →{' '}
                {(d.caches.googlePlace.formattedAddress as string) ??
                  (d.caches.googlePlace.placeName as string) ??
                  'Hit without address text'}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    );

    if (workspaceSlice && dossierTabs && dossierTabPreset === 'none') {
      return null;
    }

    const employerHeaderBlock =
      !hideEmployerHeader ? (
        d.employer ? (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {d.employer.legalName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
              Registry {d.employer.registryStatus ?? '—'}
              {d.employer.registrySource ? ` · ${d.employer.registrySource}` : ''}
            </Typography>
          </Box>
        ) : (
          <Alert severity="warning" variant="outlined">
            Employer profile missing for this verification (no registry entity linkage yet).
          </Alert>
        )
      ) : !d.employer ? (
        <Alert severity="warning" variant="outlined">
          Employer profile missing for this verification (no registry entity linkage yet).
        </Alert>
      ) : null;

    if (workspaceSlice && dossierTabs && dossierTabPreset === 'companyOnly') {
      return (
        <Stack spacing={2}>
          {employerHeaderBlock}
          {companyPanel}
        </Stack>
      );
    }

    if (workspaceSlice && dossierTabs && dossierTabPreset === 'locationOnly') {
      return (
        <Stack spacing={2}>
          {employerHeaderBlock}
          {locationPanel}
        </Stack>
      );
    }

    if (workspaceSlice && dossierTabs) {
      return (
        <Stack spacing={2}>
          {employerHeaderBlock}

          <Tabs
            value={dossierTab > 1 ? 0 : dossierTab}
            onChange={(_, v) => setDossierTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Company" />
            <Tab label="Location" />
          </Tabs>
          <Box sx={{ pt: 0.5 }}>
            {dossierTab === 0 ? companyPanel : null}
            {dossierTab === 1 ? locationPanel : null}
          </Box>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.25}>
        {!hideEmployerHeader ? (
          d.employer ? (
            <Box sx={{ py: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                {d.employer.legalName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                Registry {d.employer.registryStatus ?? '—'}
                {d.employer.registrySource ? ` · ${d.employer.registrySource}` : ''}
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning" variant="outlined">
              Employer profile missing for this verification (no registry entity linkage yet).
            </Alert>
          )
        ) : !d.employer ? (
          <Alert severity="warning" variant="outlined">
            Employer profile missing for this verification (no registry entity linkage yet).
          </Alert>
        ) : null}

        {d.employer
          ? accordionShell(
              'Company details',
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  Employer ID{' '}
                  <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: 'text.primary' }}>
                    {d.employer.ceid}
                  </Box>
                  {d.employer.sector ? ` · Industry · ${d.employer.sector}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  Registry status {d.employer.registryStatus ?? '—'} · source {d.employer.registrySource ?? '—'}
                </Typography>
              </Stack>,
            )
          : null}

        {!hideAddressAccordion ? accordionShell('Address and map', <DossierAddressMapInline d={d} />) : null}

        {d.intelligenceSnapshot
          ? accordionShell(
              'Location intelligence',
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {(d.intelligenceSnapshot.placeName as string) ?? 'Place name unavailable'}{' '}
                  {(d.intelligenceSnapshot.businessStatus as string)
                    ? `· ${String(d.intelligenceSnapshot.businessStatus)}`
                    : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  Match score {(d.intelligenceSnapshot.intelligenceScore as number) ?? '—'}
                  <br />
                  Google types:{' '}
                  {Array.isArray(d.intelligenceSnapshot.placeTypes)
                    ? (d.intelligenceSnapshot.placeTypes as string[]).join(', ')
                    : '—'}
                </Typography>
              </Stack>,
            )
          : null}

        {(d.caches.geocode || d.caches.googlePlace) &&
          accordionShell(
            'External lookups',
            <Stack spacing={1}>
              {d.caches.geocode ? (
                <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                  Geocode cache · provider {(d.caches.geocode.provider as string) ?? '—'}
                </Typography>
              ) : null}
              {d.caches.googlePlace ? (
                <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                  Google Places →{' '}
                  {(d.caches.googlePlace.formattedAddress as string) ??
                    (d.caches.googlePlace.placeName as string) ??
                    'Hit without address text'}
                </Typography>
              ) : null}
            </Stack>,
          )}

        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {dossierHints.realTimeExplainer} {dossierHints.refresh}
          </Typography>
        </Alert>
      </Stack>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
              Verification status
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, mt: 0.75 }} flexWrap="wrap">
              <Tooltip title={`Stored: ${finalOutcomeTechnical(d.verification.finalStatus)}`} placement="top" arrow>
                <Chip
                  size="small"
                  label={finalOutcomeLabelStored(d.verification.finalStatus)}
                  color={finalChip(d.verification.finalStatus)}
                  sx={{ fontWeight: 700 }}
                />
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                Attempt {d.verification.attemptNumber ?? '—'}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              component="div"
              sx={{ fontFamily: MONO, fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', lineHeight: 1.5 }}
            >
              Record ID: {d.verification.id}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.25, lineHeight: 1.55 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                Application
              </Box>{' '}
              <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600 }}>
                {d.verification.applicationId}
              </Box>
            </Typography>
            {onBackToOverview ? (
              <Button size="small" variant="outlined" sx={{ mt: 1.5 }} onClick={onBackToOverview}>
                Back to verification overview
              </Button>
            ) : null}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, borderColor: 'divider' }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
              Business on file
            </Typography>
            {d.employer ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.75 }}>
                  {d.employer.legalName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
                  Employer ID{' '}
                  <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: 'text.primary' }}>
                    {d.employer.ceid}
                  </Box>
                  {d.employer.sector ? ` · Industry · ${d.employer.sector}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Registry status {d.employer.registryStatus ?? '—'} · source {d.employer.registrySource ?? '—'}
                </Typography>
              </>
            ) : (
              <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                Employer profile missing for this verification (no registry entity linkage yet).
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, borderColor: 'divider' }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
              Work address for this check
            </Typography>
            <Tooltip title={d.verificationAddress.resolution.replace(/_/g, ' ')} placement="top" arrow>
              <Chip
                size="small"
                label={dossierResolutionLabel(d.verificationAddress.resolution)}
                color={resolutionChipTone(d.verificationAddress.resolution)}
                sx={{ mt: 0.85, mb: 1.25, fontWeight: 600 }}
              />
            </Tooltip>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.58, fontWeight: 500 }}>
              {d.verificationAddress.formattedAddress ?? d.verificationAddress.normalizedKey ?? '—'}
            </Typography>
            {d.verificationAddress.latitude != null && d.verificationAddress.longitude != null ? (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Map coordinates {d.verificationAddress.latitude.toFixed(6)}, {d.verificationAddress.longitude.toFixed(6)}
              </Typography>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }} variant="outlined">
                Coordinates missing for this verification path—the map hides until geocoding persists a latitude and longitude pair.
              </Alert>
            )}
          </CardContent>
        </Card>

        {d.intelligenceSnapshot ? (
          <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, borderColor: 'divider' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
                Location intelligence snapshot
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.85, fontWeight: 600 }}>
                {(d.intelligenceSnapshot.placeName as string) ?? 'Place name unavailable'}{' '}
                {(d.intelligenceSnapshot.businessStatus as string)
                  ? `· ${String(d.intelligenceSnapshot.businessStatus)}`
                  : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.55 }}>
                Match score {(d.intelligenceSnapshot.intelligenceScore as number) ?? '—'}
                <br />
                Google types:{' '}
                {Array.isArray(d.intelligenceSnapshot.placeTypes)
                  ? (d.intelligenceSnapshot.placeTypes as string[]).join(', ')
                  : '—'}
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {(d.caches.geocode || d.caches.googlePlace) && (
          <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, borderColor: 'divider' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
                Cached external lookups
              </Typography>
              {d.caches.geocode ? (
                <Typography variant="body2" component="div" sx={{ display: 'block', mt: 0.85, lineHeight: 1.55 }}>
                  Geocode cache · provider {(d.caches.geocode.provider as string) ?? '—'}
                </Typography>
              ) : null}
              {d.caches.googlePlace ? (
                <Typography variant="body2" component="div" sx={{ display: 'block', mt: d.caches.geocode ? 0.85 : 0.85, lineHeight: 1.55 }}>
                  Google Places&nbsp;→{' '}
                  {(d.caches.googlePlace.formattedAddress as string) ?? (d.caches.googlePlace.placeName as string) ?? 'Hit without address text'}
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        )}
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Map · {d.map.markerPolicy.replace(/_/g, ' ').toLowerCase()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.55 }}>
              {dossierHints.mapSingleSite}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {marker ? (
              <CompanyVerificationMap
                lat={marker.lat}
                lng={marker.lng}
                title={marker.label}
                formattedAddress={d.verificationAddress.formattedAddress}
              />
            ) : (
              <Box sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  Pin needs saved coordinates—run geocoding or attach a persisted location version for the work address above.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} variant="outlined">
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {dossierHints.realTimeExplainer} {dossierHints.refresh}
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  );
}
